import * as THREE from "three";

export const LANE_X = [-7.2, -2.4, 2.4, 7.2];
export const SPAWN_Z = -22;
export const PLAYER_Z = 11;
export const BREACH_Z = 13.2;
export const LANE_COLORS = [0x3df0ff, 0xff2d95, 0xffb347, 0xc6ff4d];

function edges(geo: THREE.BufferGeometry, color: number): THREE.LineSegments {
  return new THREE.LineSegments(
    new THREE.EdgesGeometry(geo, 15),
    new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.95 }),
  );
}

function fill(geo: THREE.BufferGeometry, color = 0x070a12): THREE.Mesh {
  return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.82 }));
}

export function neonGroup(geo: THREE.BufferGeometry, color: number, fillColor = 0x070a12): THREE.Group {
  const g = new THREE.Group();
  g.add(fill(geo, fillColor), edges(geo, color));
  return g;
}

export type SwarmKind = "drone" | "striker" | "heavy";

export function makeSkiff(): THREE.Group {
  const g = makePlayer();
  g.scale.setScalar(1.35);
  g.position.set(0, 0.55, 0.6);
  return g;
}

export function makeSwarmEnemy(kind: SwarmKind): THREE.Group {
  if (kind === "striker") {
    const g = neonGroup(new THREE.TetrahedronGeometry(0.55), 0xffb347);
    g.scale.set(0.85, 0.85, 1.3);
    return g;
  }
  if (kind === "heavy") return neonGroup(new THREE.BoxGeometry(1.15, 0.7, 1.15), 0xff3b5c);
  return neonGroup(new THREE.OctahedronGeometry(0.62), 0xff2d95);
}

export function makePlayer(): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.ConeGeometry(0.7, 2.6, 3);
  body.rotateX(-Math.PI / 2);
  g.add(fill(body), edges(body, 0x3df0ff));
  const wing = new THREE.BoxGeometry(2.6, 0.12, 0.7);
  const wingMesh = fill(wing);
  const wingEdge = edges(wing, 0xff2d95);
  wingMesh.position.z = 0.35;
  wingEdge.position.z = 0.35;
  g.add(wingMesh, wingEdge);
  const nacelle = new THREE.CylinderGeometry(0.14, 0.2, 0.9, 6);
  for (const x of [-0.7, 0.7]) {
    const m = fill(nacelle, 0x05070c);
    const e = edges(nacelle, 0xffb347);
    m.rotation.x = Math.PI / 2;
    e.rotation.x = Math.PI / 2;
    m.position.set(x, -0.15, 0.7);
    e.position.set(x, -0.15, 0.7);
    g.add(m, e);
  }
  const light = new THREE.PointLight(0x3df0ff, 2.4, 12);
  light.position.set(0, 0.4, -0.4);
  g.add(light);
  g.position.set(0, 0.8, PLAYER_Z);
  return g;
}

const ENEMY_SHAPES = [
  () => new THREE.OctahedronGeometry(0.95),
  () => new THREE.IcosahedronGeometry(0.9, 0),
  () => new THREE.TetrahedronGeometry(1.05),
  () => new THREE.DodecahedronGeometry(0.85, 0),
];

export function makeEnemy(lane: number): THREE.Group {
  const g = new THREE.Group();
  const geo = ENEMY_SHAPES[lane % ENEMY_SHAPES.length]();
  const color = LANE_COLORS[lane];
  g.add(fill(geo), edges(geo, color));
  const ring = new THREE.TorusGeometry(1.15, 0.035, 8, 24);
  g.add(fill(ring, 0x05060a), edges(ring, color));
  const hit = new THREE.Mesh(
    new THREE.SphereGeometry(1.9, 8, 8),
    new THREE.MeshBasicMaterial({ visible: false }),
  );
  hit.name = "hit-volume";
  g.add(hit);
  g.position.set(LANE_X[lane], 1.2, SPAWN_Z);
  return g;
}

export function makeLabel(key: string, value: number, color: string): THREE.Sprite {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext("2d")!;
  ctx.clearRect(0, 0, 256, 256);
  ctx.strokeStyle = color;
  ctx.lineWidth = 6;
  ctx.strokeRect(18, 18, 220, 220);
  ctx.strokeRect(28, 28, 200, 200);
  ctx.fillStyle = "rgba(5,6,10,0.72)";
  ctx.fillRect(32, 32, 192, 192);
  ctx.fillStyle = color;
  ctx.font = "bold 72px Orbitron, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(key.toUpperCase(), 128, 92);
  ctx.fillStyle = "#ffb347";
  ctx.font = "bold 64px Orbitron, sans-serif";
  ctx.fillText(String(value), 128, 168);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }),
  );
  sprite.scale.set(3.2, 3.2, 1);
  sprite.position.y = 2.4;
  return sprite;
}

export function makeBeam(from: THREE.Vector3, to: THREE.Vector3, color: number): THREE.Mesh {
  const dir = new THREE.Vector3().subVectors(to, from);
  const len = Math.max(0.2, dir.length());
  const geo = new THREE.CylinderGeometry(0.06, 0.02, len, 6);
  const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.95 });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.copy(from).addScaledVector(dir, 0.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
  return mesh;
}

export function disposeGroup(root: THREE.Object3D): void {
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const material = (obj as THREE.Mesh).material;
    if (!material) return;
    const mats = Array.isArray(material) ? material : [material];
    for (const mat of mats) {
      const mapped = mat as THREE.MeshBasicMaterial;
      mapped.map?.dispose();
      mat.dispose();
    }
  });
}

export class Sparks {
  private items: { mesh: THREE.Points; age: number; life: number }[] = [];

  constructor(private scene: THREE.Scene) {}

  burst(origin: THREE.Vector3, color: number): void {
    const n = 28;
    const pos = new Float32Array(n * 3);
    const vel: THREE.Vector3[] = [];
    for (let i = 0; i < n; i++) {
      pos[i * 3] = origin.x;
      pos[i * 3 + 1] = origin.y;
      pos[i * 3 + 2] = origin.z;
      vel.push(
        new THREE.Vector3((Math.random() - 0.5) * 18, Math.random() * 10, (Math.random() - 0.5) * 18),
      );
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.userData.vel = vel;
    const mesh = new THREE.Points(
      geo,
      new THREE.PointsMaterial({ color, size: 0.22, transparent: true, opacity: 1, depthWrite: false }),
    );
    this.scene.add(mesh);
    this.items.push({ mesh, age: 0, life: 0.45 });
  }

  update(dt: number): void {
    for (const item of this.items) {
      item.age += dt;
      const pos = item.mesh.geometry.getAttribute("position") as THREE.BufferAttribute;
      const vel = item.mesh.geometry.userData.vel as THREE.Vector3[];
      for (let i = 0; i < vel.length; i++) {
        vel[i].y -= 12 * dt;
        pos.setXYZ(i, pos.getX(i) + vel[i].x * dt, pos.getY(i) + vel[i].y * dt, pos.getZ(i) + vel[i].z * dt);
      }
      pos.needsUpdate = true;
      const mat = item.mesh.material as THREE.PointsMaterial;
      mat.opacity = Math.max(0, 1 - item.age / item.life);
    }
    this.items = this.items.filter((item) => {
      if (item.age < item.life) return true;
      this.scene.remove(item.mesh);
      item.mesh.geometry.dispose();
      (item.mesh.material as THREE.Material).dispose();
      return false;
    });
  }
}
